import { useState, useEffect } from "react";
import {
  Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle, Zap, Sparkles,
} from "lucide-react";
import { llmProviderService, LlmProvider, AtivoEfetivo } from "../../../../services/llmProviderService";

const TIPO_LABELS: Record<string, string> = {
  "openai-compatible": "OpenAI-compatible",
  "ollama-native": "Ollama nativa",
};

export function LlmProviders() {
  const [providers, setProviders] = useState<LlmProvider[]>([]);
  const [ativoEfetivo, setAtivoEfetivo] = useState<AtivoEfetivo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<LlmProvider | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [formData, setFormData] = useState({ nome: "", url: "", model: "", apiKey: "", tipo: "openai-compatible", ativo: false });

  useEffect(() => { loadProviders(); }, []);

  const loadProviders = async () => {
    setIsLoading(true);
    try {
      const [list, ativo] = await Promise.all([
        llmProviderService.list(),
        llmProviderService.ativo(),
      ]);
      setProviders(list);
      setAtivoEfetivo(ativo);
    } catch (error) {
      console.error("Erro ao carregar provedores:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        nome: formData.nome,
        url: formData.url,
        model: formData.model,
        apiKey: formData.apiKey || null,
        tipo: formData.tipo,
        ativo: formData.ativo,
      };
      if (editing) {
        await llmProviderService.update(editing.id, payload);
        setMessage({ type: "success", text: "Provedor atualizado com sucesso!" });
      } else {
        await llmProviderService.create(payload);
        setMessage({ type: "success", text: "Provedor criado com sucesso!" });
      }
      closeModal();
      loadProviders();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao salvar provedor" });
    }
  };

  const handleEdit = (provider: LlmProvider) => {
    setEditing(provider);
    setFormData({
      nome: provider.nome,
      url: provider.url,
      model: provider.model,
      apiKey: provider.apiKey || "",
      tipo: provider.tipo,
      ativo: provider.ativo,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Deseja remover este provedor?")) return;
    try {
      await llmProviderService.remove(id);
      setMessage({ type: "success", text: "Provedor removido!" });
      loadProviders();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao remover" });
    }
  };

  const handleAtivar = async (id: string) => {
    setActivatingId(id);
    setMessage(null);
    try {
      await llmProviderService.setAtivo(id);
      setMessage({ type: "success", text: "Provedor definido como em uso!" });
      loadProviders();
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao ativar provedor" });
    } finally {
      setActivatingId(null);
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    setMessage(null);
    try {
      const result = await llmProviderService.test(id);
      setMessage({
        type: result.ok ? "success" : "error",
        text: result.ok ? result.message || "Conexão OK" : `Falha: ${result.erro || "sem resposta"}`,
      });
    } catch (error: any) {
      setMessage({ type: "error", text: error.response?.data?.error || "Erro ao testar conexão" });
    } finally {
      setTestingId(null);
    }
  };

  const openNewModal = () => {
    setEditing(null);
    setFormData({ nome: "", url: "", model: "", apiKey: "", tipo: "openai-compatible", ativo: false });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {ativoEfetivo?.provider && (
        <div className={`p-4 rounded-lg flex items-center gap-3 border ${ativoEfetivo.fonte === 'env' ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-green-50 text-green-800 border-green-200"}`}>
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">
              Em uso no momento: {ativoEfetivo.provider.nome} — {ativoEfetivo.provider.model}
            </p>
            {ativoEfetivo.fonte === 'env' && (
              <p className="text-sm mt-0.5">
                Definida via variável de ambiente (.env) e não listada acima. Cadastre e ative um provedor para gerenciá-lo por aqui.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" /> APIs de IA
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Cadastre provedores de IA (Gemini, DeepSeek, OpenAI, Ollama) e defina qual será usado na geração de propostas.
          </p>
        </div>
        <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 btn btn-primary">
          <Plus className="w-4 h-4" /> Nova API
        </button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
        ) : providers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Nenhum provedor cadastrado. Adicione o primeiro para gerar propostas com IA.
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Modelo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {providers.map((provider) => (
                <tr key={provider.id} className="hover:bg-muted/50">
                  <td className="px-4 py-4 font-medium text-foreground">{provider.nome}</td>
                  <td className="px-4 py-4 text-muted-foreground text-sm">{provider.model}</td>
                  <td className="px-4 py-4 text-muted-foreground text-sm">{TIPO_LABELS[provider.tipo] || provider.tipo}</td>
                  <td className="px-4 py-4">
                    {provider.ativo ? (
                      <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-700 dark:text-green-400 font-medium">Em uso</span>
                    ) : (
                      <button
                        onClick={() => handleAtivar(provider.id)}
                        disabled={activatingId === provider.id}
                        className="px-2 py-1 rounded-full text-xs border border-border text-muted-foreground hover:text-foreground hover:border-foreground disabled:opacity-50"
                      >
                        {activatingId === provider.id ? "Ativando..." : "Definir em uso"}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleTest(provider.id)} title="Testar conexão" className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-500/10 rounded-lg">
                        {testingId === provider.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(provider)} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(provider.id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Modal de criação/edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{editing ? "Editar API de IA" : "Nova API de IA"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Nome *</label>
                <input type="text" value={formData.nome} onChange={(e) => setFormData(p => ({ ...p, nome: e.target.value }))} required placeholder="Ex: DeepSeek"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">URL *</label>
                <input type="text" value={formData.url} onChange={(e) => setFormData(p => ({ ...p, url: e.target.value }))} required placeholder="https://api.deepseek.com/v1/chat/completions"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Modelo *</label>
                <input type="text" value={formData.model} onChange={(e) => setFormData(p => ({ ...p, model: e.target.value }))} required placeholder="deepseek-chat"
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tipo *</label>
                <select value={formData.tipo} onChange={(e) => setFormData(p => ({ ...p, tipo: e.target.value }))}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground">
                  <option value="openai-compatible">OpenAI-compatible</option>
                  <option value="ollama-native">Ollama nativa</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">API Key</label>
                <input type="text" value={formData.apiKey} onChange={(e) => setFormData(p => ({ ...p, apiKey: e.target.value }))} placeholder="sk-..."
                  className="w-full px-3 py-2 border border-input rounded-lg bg-card text-foreground" />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                <input type="checkbox" checked={formData.ativo} onChange={(e) => setFormData(p => ({ ...p, ativo: e.target.checked }))} />
                Definir como em uso
              </label>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeModal} className="flex-1 py-2 btn btn-outline">Cancelar</button>
                <button type="submit" className="flex-1 py-2 btn btn-primary">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
